/**
 * Passport Certifications Section
 * Owner: Add via photo upload/camera → AI parse → review & confirm. Edit/delete existing.
 * Non-owner: metadata only + "Document on file" badge via has_media.
 * 
 * The "Add" flow requires a photo of the actual certification document to prevent fraud.
 */

import { useRef, useState } from 'react';
import { PassportSectionFallback } from './PassportSectionFallback';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { describeTrust, certificationStatus, describeStatus, isCertificationErasable, localTodayIso } from '@/lib/passport/certificationTrust';
import { useCredentialExpiringWindow } from '@/lib/passport/useCredentialExpiringWindow';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Award, Plus, Pencil, Trash2, Eye, EyeOff, FileCheck, Loader2, Camera, ImagePlus, AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  usePassportCertifications,
  useCreateCertification,
  useUpdateCertification,
  useDeleteCertification,
  useUploadCertificationMedia,
} from '@/hooks/usePassportSections';
import { CertificationUploader } from './CertificationUploader';

interface Props {
  userId: string;
  isOwner: boolean;
  isReadOnly?: boolean;
}

interface CertFormData {
  title: string;
  issuer: string;
  issued_date: string;
  expires_date: string;
  credential_id: string;
  notes: string;
  visibility: string;
}

const emptyForm: CertFormData = {
  title: '', issuer: '', issued_date: '', expires_date: '',
  credential_id: '', notes: '', visibility: 'private',
};

type AddStep = 'choose' | 'analyzing' | 'review';

export function PassportCertificationsSection({ userId, isOwner, isReadOnly }: Props) {
  // Core-100 R3 (OD-11): null until the platform's expiring window is set — then no credential is called expiring.
  const expiringWindowDays = useCredentialExpiringWindow();
  const { data: certs, isLoading, isError } = usePassportCertifications(userId, { visibleOnly: !isOwner });
  const createCert = useCreateCertification();
  const updateCert = useUpdateCertification();
  const deleteCert = useDeleteCertification();
  const uploadMedia = useUploadCertificationMedia();

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<CertFormData>(emptyForm);

  // Add flow
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addStep, setAddStep] = useState<AddStep>('choose');
  const [parsedForm, setParsedForm] = useState<CertFormData>(emptyForm);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [aiConfidence, setAiConfidence] = useState<number>(0);
  // Core-100 R2 (CORE-L): the server-recorded assessment this credential will cite (document-supported).
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // ─── Add Flow ─────────────────────────────────────────────────────────────

  const openAdd = () => {
    setAddStep('choose');
    setParsedForm(emptyForm);
    setCapturedFile(null);
    setAiConfidence(0);
    setAddDialogOpen(true);
  };

  const handleFileCapture = async (file: File) => {
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF');
      return;
    }

    setCapturedFile(file);
    setAddStep('analyzing');
    setIsAnalyzing(true);

    try {
      // CORE-BK: the Core image boundary accepts raster only, and refuses
      // anything else rather than forwarding it. A PDF is therefore rendered to
      // an image here first — the same pdfjs pattern ToolImportDialog already
      // uses for purchase documents — so a credential PDF keeps working.
      //
      // This conversion is a format step, not a privacy control: the client is
      // untrusted, so the server still verifies the bytes it receives and still
      // minimises them. If a caller sends a PDF anyway, it is refused there.
      let base64: string;
      let outboundMimeType: string;

      if (file.type === 'application/pdf') {
        const arrayBuffer = await file.arrayBuffer();
        const pdfjsLib: any = await import('pdfjs-dist/build/pdf.mjs');
        const worker = await import('pdfjs-dist/build/pdf.worker.mjs?url');
        pdfjsLib.GlobalWorkerOptions.workerSrc = worker.default;
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d')!;
        await page.render({ canvasContext: ctx, viewport }).promise;
        base64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
        canvas.width = 0;
        canvas.height = 0;
        outboundMimeType = 'image/jpeg';
      } else {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        base64 = btoa(binary);
        outboundMimeType = file.type;
      }

      const { data, error } = await supabase.functions.invoke('parse-certification-photo', {
        body: { image: base64, mimeType: outboundMimeType },
      });

      if (error) throw error;

      if (data.error) {
        toast.error(data.error);
        setAddStep('choose');
        setIsAnalyzing(false);
        return;
      }

      setParsedForm({
        title: data.title || '',
        issuer: data.issuer || '',
        issued_date: data.issued_date || '',
        expires_date: data.expires_date || '',
        credential_id: data.credential_id || '',
        notes: '',
        visibility: 'private',
      });
      setAiConfidence(data.confidence || 0);
      setAssessmentId(typeof data.assessment_id === 'string' ? data.assessment_id : null);
      setAddStep('review');
    } catch (err) {
      console.error('Certification parse error:', err);
      toast.error('Failed to analyze certification. Please try again.');
      setAddStep('choose');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAddFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileCapture(file);
    e.target.value = '';
  };

  const handleConfirmAdd = async () => {
    if (!parsedForm.title.trim()) {
      toast.error('Certification title is required');
      return;
    }

    const payload = {
      user_id: userId,
      title: parsedForm.title.trim(),
      issuer: parsedForm.issuer.trim() || undefined,
      issued_date: parsedForm.issued_date || undefined,
      expires_date: parsedForm.expires_date || undefined,
      credential_id: parsedForm.credential_id.trim() || undefined,
      notes: parsedForm.notes.trim() || undefined,
      visibility: parsedForm.visibility,
      ...(assessmentId ? { document_assessment_id: assessmentId } : {}),
    };

    createCert.mutate(payload, {
      onSuccess: (newCert) => {
        // Also upload the original photo as evidence
        if (capturedFile && newCert?.id) {
          uploadMedia.mutate({
            file: capturedFile,
            certId: newCert.id,
            userId,
          });
        }
        setAddDialogOpen(false);
      },
    });
  };

  // ─── Edit Flow ────────────────────────────────────────────────────────────

  const openEdit = (cert: any) => {
    setEditForm({
      title: cert.title,
      issuer: cert.issuer || '',
      issued_date: cert.issued_date || '',
      expires_date: cert.expires_date || '',
      credential_id: cert.credential_id || '',
      notes: cert.notes || '',
      visibility: cert.visibility || 'private',
    });
    setEditingId(cert.id);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editForm.title.trim() || !editingId) return;
    updateCert.mutate({
      id: editingId,
      title: editForm.title.trim(),
      issuer: editForm.issuer.trim() || undefined,
      issued_date: editForm.issued_date || undefined,
      expires_date: editForm.expires_date || undefined,
      credential_id: editForm.credential_id.trim() || undefined,
      notes: editForm.notes.trim() || undefined,
      visibility: editForm.visibility,
    }, { onSuccess: () => setEditDialogOpen(false) });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Certifications</CardTitle>
            {certs && <Badge variant="secondary" className="text-xs">{certs.length}</Badge>}
          </div>
          {isOwner && !isReadOnly && (
            <Button variant="outline" size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* CORE-C GAP-003: a failed read is not a statement that the person
            has none. Checked BEFORE the empty branch, which is where the
            failure used to land. */}
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : isError ? (
          <PassportSectionFallback sectionName="Certifications" inline />
        ) : !certs?.length ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {isOwner ? 'No certifications yet. Add your first one by uploading a photo.' : 'No certifications to display.'}
          </p>
        ) : (
          certs.map((cert) => (
            <div key={cert.id} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm">{cert.title}</p>
                    {isOwner && (
                      <Badge variant={cert.visibility === 'visible_to_shops' ? 'default' : 'outline'} className="text-[10px]">
                        {cert.visibility === 'visible_to_shops' ? <Eye className="h-3 w-3 mr-0.5" /> : <EyeOff className="h-3 w-3 mr-0.5" />}
                        {cert.visibility === 'visible_to_shops' ? 'Visible' : 'Private'}
                      </Badge>
                    )}
                    {/* Core-100 R2 (CORE-L): the trust state, never flattened and never worded as doubt. */}
                    <Badge variant="outline" className="text-[10px]" data-testid="cert-trust">
                      {(cert as any).trust_state === 'document_supported' && <FileCheck className="h-3 w-3 mr-0.5" />}
                      {describeTrust((cert as any).trust_state, { assessmentMatches: (cert as any).assessment_matches, attestedByKind: (cert as any).attested_by_kind })}
                    </Badge>
                    {!isOwner && (cert as any).has_media && (cert as any).trust_state !== 'document_supported' && (
                      <Badge variant="secondary" className="text-[10px]">
                        <FileCheck className="h-3 w-3 mr-0.5" /> Document on file
                      </Badge>
                    )}
                    {(() => {
                      const status = describeStatus(certificationStatus(cert.expires_date, (cert as any).revoked_at, (cert as any).superseded_by, localTodayIso(new Date()), expiringWindowDays));
                      return status ? <Badge variant="secondary" className="text-[10px]" data-testid="cert-status">{status}</Badge> : null;
                    })()}
                  </div>
                  {cert.issuer && <p className="text-xs text-muted-foreground">{cert.issuer}</p>}
                  <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                    {cert.issued_date && <span>Issued: {format(new Date(cert.issued_date), 'MMM yyyy')}</span>}
                    {cert.expires_date && <span>Expires: {format(new Date(cert.expires_date), 'MMM yyyy')}</span>}
                  </div>
                  {cert.credential_id && <p className="text-xs text-muted-foreground">ID: {cert.credential_id}</p>}
                </div>
                {isOwner && !isReadOnly && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(cert)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {/* Core-100 R4 CORE-L: an attested or revoked credential stays on the record (hide it instead). */}
                    {isCertificationErasable(cert as { trust_state?: string | null; revoked_at?: string | null }) && (
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7 text-destructive" aria-label="Delete credential"
                        onClick={() => deleteCert.mutate({ id: cert.id, userId })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {isOwner && (
                <CertificationUploader
                  certificationId={cert.id}
                  userId={userId}
                  disabled={isReadOnly}
                />
              )}
            </div>
          ))
        )}
      </CardContent>

      {/* ─── Add Certification Dialog ──────────────────────────────────────── */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => { if (!isAnalyzing) setAddDialogOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Certification</DialogTitle>
            <DialogDescription>
              Upload or photograph your certification document for verification.
            </DialogDescription>
          </DialogHeader>

          {addStep === 'choose' && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground text-center">
                To prevent fraud, certifications must be added by uploading a photo or taking a picture of the actual document.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="h-24 flex-col gap-2"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <Camera className="h-6 w-6" />
                  <span className="text-sm">Take Photo</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-24 flex-col gap-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImagePlus className="h-6 w-6" />
                  <span className="text-sm">Upload Image</span>
                </Button>
              </div>
            </div>
          )}

          {addStep === 'analyzing' && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Analyzing your certification document...</p>
            </div>
          )}

          {addStep === 'review' && (
            <div className="space-y-3">
              {aiConfidence < 0.5 && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20">
                  <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-destructive">
                    Low confidence — this may not be a valid certification document. Please review the details carefully.
                  </p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Title *</Label>
                <Input value={parsedForm.title} onChange={(e) => setParsedForm({ ...parsedForm, title: e.target.value })} placeholder="e.g. ASE A1" />
              </div>
              <div className="space-y-1.5">
                <Label>Issuer</Label>
                <Input value={parsedForm.issuer} onChange={(e) => setParsedForm({ ...parsedForm, issuer: e.target.value })} placeholder="e.g. ASE" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Issued Date</Label>
                  <Input type="date" value={parsedForm.issued_date} onChange={(e) => setParsedForm({ ...parsedForm, issued_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Expires Date</Label>
                  <Input type="date" value={parsedForm.expires_date} onChange={(e) => setParsedForm({ ...parsedForm, expires_date: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Credential ID</Label>
                <Input value={parsedForm.credential_id} onChange={(e) => setParsedForm({ ...parsedForm, credential_id: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea value={parsedForm.notes} onChange={(e) => setParsedForm({ ...parsedForm, notes: e.target.value })} rows={2} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Visible to shops</Label>
                <Switch
                  checked={parsedForm.visibility === 'visible_to_shops'}
                  onCheckedChange={(checked) => setParsedForm({ ...parsedForm, visibility: checked ? 'visible_to_shops' : 'private' })}
                />
              </div>
            </div>
          )}

          {addStep === 'review' && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddStep('choose')}>Re-upload</Button>
              <Button onClick={handleConfirmAdd} disabled={!parsedForm.title.trim() || createCert.isPending}>
                {createCert.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Confirm & Save
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Hidden file inputs for add flow */}
      <input ref={fileInputRef} type="file" accept="image/*,application/pdf" onChange={handleAddFileChange} className="hidden" />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleAddFileChange} className="hidden" />

      {/* ─── Edit Certification Dialog ─────────────────────────────────────── */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Certification</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title *</Label>
              <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} placeholder="e.g. ASE A1" />
            </div>
            <div className="space-y-1.5">
              <Label>Issuer</Label>
              <Input value={editForm.issuer} onChange={(e) => setEditForm({ ...editForm, issuer: e.target.value })} placeholder="e.g. ASE" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Issued Date</Label>
                <Input type="date" value={editForm.issued_date} onChange={(e) => setEditForm({ ...editForm, issued_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Expires Date</Label>
                <Input type="date" value={editForm.expires_date} onChange={(e) => setEditForm({ ...editForm, expires_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Credential ID</Label>
              <Input value={editForm.credential_id} onChange={(e) => setEditForm({ ...editForm, credential_id: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={2} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Visible to shops</Label>
              <Switch
                checked={editForm.visibility === 'visible_to_shops'}
                onCheckedChange={(checked) => setEditForm({ ...editForm, visibility: checked ? 'visible_to_shops' : 'private' })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={!editForm.title.trim() || updateCert.isPending}>
              {updateCert.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
